import React, { useEffect, useState, useRef } from 'react';
import { Settings, Building2, DollarSign, Bell, Printer, Save, Upload, Image as ImageIcon } from 'lucide-react';
import { settingsService } from '../../api/services';
import { Button, FormField, Input, Select } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import toast from 'react-hot-toast';

const SETTING_GROUPS = [
  {
    key: 'company', label: 'Pharmacy Info', icon: Building2,
    fields: [
      { key: 'company_name',       label: 'Pharmacy Name',    type: 'text',   placeholder: 'e.g. Noorullah Stanikzai Pharmacy' },
      { key: 'company_name_dari',  label: 'Pharmacy Name (Dari/Pashto)', type: 'text', placeholder: 'نور الله ستانکزی درملتون' },
      { key: 'company_address',    label: 'Address',          type: 'text',   placeholder: 'Kabul, Afghanistan' },
      { key: 'company_phone',      label: 'Phone 1',          type: 'text',   placeholder: '+93 700 000 000' },
      { key: 'company_phone2',     label: 'Phone 2',          type: 'text',   placeholder: '+93 700 000 001' },
      { key: 'company_email',      label: 'Email',            type: 'email',  placeholder: 'email@pharmacy.com' },
      { key: 'company_website',    label: 'Website',          type: 'text',   placeholder: 'www.pharmacy.com' },
      { key: 'company_license',    label: 'License Number',   type: 'text',   placeholder: 'License no.' },
      { key: 'company_tax_number', label: 'Tax Number',       type: 'text',   placeholder: 'Tax ID' },
    ]
  },
  {
    key: 'finance', label: 'Finance', icon: DollarSign,
    fields: [
      { key: 'default_currency',    label: 'Default Currency',      type: 'select', options: ['AFN','USD','EUR','PKR','IRR'] },
      { key: 'currency_symbol',     label: 'Currency Symbol',       type: 'text',   placeholder: 'e.g. AFN or ؋' },
      { key: 'default_tax_rate',    label: 'Default Tax Rate (%)',  type: 'number', placeholder: '0' },
      { key: 'low_stock_threshold', label: 'Low Stock Alert at',    type: 'number', placeholder: '10' },
      { key: 'expiry_alert_days',   label: 'Expiry Alert (days)',   type: 'number', placeholder: '90' },
    ]
  },
  {
    key: 'notifications', label: 'Notifications', icon: Bell,
    fields: [
      { key: 'notify_low_stock',    label: 'Low Stock Alerts',          type: 'checkbox' },
      { key: 'notify_expiry',       label: 'Expiry Alerts',             type: 'checkbox' },
      { key: 'notify_customer_debt',label: 'Customer Debt Reminders',   type: 'checkbox' },
      { key: 'notify_supplier_due', label: 'Supplier Payment Alerts',   type: 'checkbox' },
    ]
  },
  {
    key: 'printing', label: 'Invoice & Printing', icon: Printer,
    fields: [
      { key: 'invoice_title',       label: 'Invoice Title (Dari)',  type: 'text',     placeholder: 'فاکتور فروش' },
      { key: 'invoice_footer',      label: 'Invoice Footer Text',  type: 'text',     placeholder: 'ممنون از خرید شما' },
      { key: 'invoice_note',        label: 'Default Invoice Note', type: 'text',     placeholder: 'Optional note' },
      { key: 'show_logo_on_invoice',label: 'Show Logo on Invoice', type: 'checkbox' },
      { key: 'show_signature_line', label: 'Show Signature Lines', type: 'checkbox' },
    ]
  },
];

export default function SettingsPage() {
  const [activeGroup, setActiveGroup] = useState('company');
  const [values, setValues]           = useState({});
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile]       = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    settingsService.getAll()
      .then(res => {
        const s = res.data.settings || {};
        setValues(s);
        if (s.company_logo) setLogoPreview(s.company_logo);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target.result;
      setLogoPreview(b64);
      setValues(v => ({ ...v, company_logo: b64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsService.updateAll(values);
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const group = SETTING_GROUPS.find(g => g.key === activeGroup);

  return (
    <div>
      <Breadcrumb />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
            <Settings size={16} className="text-gray-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">System Settings</h1>
            <p className="text-sm text-gray-500">Configure pharmacy system and invoice settings</p>
          </div>
        </div>
        <Button onClick={handleSave} loading={saving}>
          <Save size={16} /> Save All Changes
        </Button>
      </div>

      <div className="flex gap-6">
        {/* Sidebar */}
        <div className="w-52 flex-shrink-0 space-y-1">
          {SETTING_GROUPS.map(g => (
            <button key={g.key} onClick={() => setActiveGroup(g.key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeGroup === g.key ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}>
              <g.icon size={16} />
              {g.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 bg-white rounded-xl border border-gray-200 p-6">
          {loading ? (
            <div className="space-y-4">{[1,2,3,4].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
                {group && <group.icon size={18} className="text-blue-600" />}
                <h2 className="text-base font-semibold text-gray-900">{group?.label} Settings</h2>
              </div>

              {/* Logo Upload — only in company tab */}
              {activeGroup === 'company' && (
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Pharmacy Logo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-32 h-20 border-2 border-dashed border-gray-200 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-contain p-1" />
                      ) : (
                        <div className="text-center">
                          <ImageIcon size={24} className="text-gray-300 mx-auto mb-1" />
                          <p className="text-xs text-gray-400">No logo</p>
                        </div>
                      )}
                    </div>
                    <div>
                      <button onClick={() => fileRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700">
                        <Upload size={15} /> Upload Logo
                      </button>
                      <p className="text-xs text-gray-400 mt-1.5">PNG or JPG. Shown on invoices.</p>
                      {logoPreview && (
                        <button onClick={() => { setLogoPreview(null); setLogoFile(null); setValues(v => ({ ...v, company_logo: '' })); }}
                          className="text-xs text-red-500 hover:text-red-700 mt-1">Remove logo</button>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                    </div>
                  </div>
                </div>
              )}

              {/* Fields */}
              {group?.fields.map(field => (
                <FormField key={field.key} label={field.label}>
                  {field.type === 'checkbox' ? (
                    <label className="flex items-center gap-2 cursor-pointer mt-1">
                      <input type="checkbox"
                        checked={values[field.key] === true || values[field.key] === 'true'}
                        onChange={e => setValues({ ...values, [field.key]: e.target.checked })}
                        className="rounded w-4 h-4 text-blue-600" />
                      <span className="text-sm text-gray-600">Enable {field.label}</span>
                    </label>
                  ) : field.type === 'select' ? (
                    <Select value={values[field.key] || ''} onChange={e => setValues({ ...values, [field.key]: e.target.value })}>
                      <option value="">Select...</option>
                      {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </Select>
                  ) : (
                    <Input type={field.type} value={values[field.key] || ''} onChange={e => setValues({ ...values, [field.key]: e.target.value })} placeholder={field.placeholder} />
                  )}
                </FormField>
              ))}
            </div>
          )}
        </div>

        {/* Backup Section */}
        <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Database Backup</h3>
          <p className="text-xs text-gray-400 mb-4">Download a full backup of all pharmacy data as JSON file.</p>
          <button
            onClick={async () => {
              try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch('/api/backup/download', {
                  headers: { Authorization: `Bearer ${token}` }
                });
                if (!res.ok) throw new Error('Failed');
                const blob = await res.blob();
                const url  = URL.createObjectURL(blob);
                const a    = document.createElement('a');
                a.href = url;
                a.download = `pharmacy-backup-${new Date().toISOString().split('T')[0]}.json`;
                a.click();
                URL.revokeObjectURL(url);
              } catch { alert('Backup failed. Make sure you are logged in as admin.'); }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
            ⬇️ Download Backup Now
          </button>
          <p className="text-xs text-gray-400 mt-2">Tip: Download a backup daily and keep it safe.</p>
        </div>
      </div>
    </div>
  );
}
